import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useLogger } from "../services/logging";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery, useLazyQuery } from "@apollo/client";
import { GET_ME } from "../graphql/users";
import { GET_TRADE_MARKET_DATA, GET_MARKET_QUOTE } from "../graphql/positions";
import { PositionType, PositionStatus } from "../types/position";
import { usePositionValidation } from "../hooks/useValidation";
import ValidationStatusBar from "./ValidationStatusBar";
import BalanceChecker from "./BalanceChecker";
import TradeStatusIndicator from "./TradeStatusIndicator";

export interface PositionFormValues {
  id?: string;
  type: PositionType;
  quantity: number;
  price: number;
  date: Date | null;
  notes?: string;
  status?: PositionStatus;
}

interface PositionFormDialogProps {
  open: boolean;
  onClose: () => void;
  initialValues?: PositionFormValues;
  onSave: (values: PositionFormValues) => void;
  tradeId?: number; // Optional: tradeId when editing existing trade
  symbol?: string; // Optional: symbol for fetching market data when adding new trade
  marketContext?: any; // Optional: market context passed from parent (TradeFormDialog)
}

const POSITION_TYPES: { value: PositionType; label: string }[] = [
  { value: "ENTRY", label: "Entry" },
  { value: "EXIT", label: "Exit" },
  { value: "PARTIAL_EXIT", label: "Partial Exit" },
  { value: "ADD_TO_POSITION", label: "Add to Position" },
];

const PositionFormDialog: React.FC<PositionFormDialogProps> = ({
  open,
  onClose,
  initialValues,
  onSave,
  tradeId,
  symbol,
  marketContext,
}) => {
  const logger = useLogger("PositionFormDialog");
  // Fetch user data to get dynamic fee settings
  const { data: userData, loading: userLoading } = useQuery(GET_ME);

  // Query: Fetch market data by tradeId (when editing existing trade)
  const {
    data: marketData,
    loading: marketDataLoading,
    error: marketDataError,
  } = useQuery(GET_TRADE_MARKET_DATA, {
    variables: { tradeId: tradeId ?? 0 },
    skip: !open || !tradeId, // Only fetch when dialog is open and tradeId is available
  });

  const [formData, setFormData] = useState<PositionFormValues>({
    type: "ENTRY",
    quantity: 0,
    price: 0,
    date: new Date(),
    notes: "",
  });

  const [marketPrice, setMarketPrice] = useState<number | null>(null);
  const [priceEditedByUser, setPriceEditedByUser] = useState(false);
  const [symbolMarketDataLoading, setSymbolMarketDataLoading] = useState(false);
  const [symbolMarketDataError, setSymbolMarketDataError] =
    useState<Error | null>(null);

  // Use validation hook
  const {
    validation,
    balanceResult,
    isTradeClosed,
    isLoading: validationLoading,
    clearValidation,
  } = usePositionValidation(tradeId ?? 0, formData.quantity, formData.price);

  // Update market price when market data is fetched from GraphQL (editing case with tradeId)
  useEffect(() => {
    if (marketData?.trade?.marketContext?.currentPrice != null) {
      setMarketPrice(marketData.trade.marketContext.currentPrice);
      logger.debug(
        "Market price fetched and set from GraphQL",
        { method: "useEffect" },
        { currentPrice: marketData.trade.marketContext.currentPrice },
      );
    }
  }, [marketData, logger]);

  // Use market price from parent if provided (passed from TradeFormDialog)
  useEffect(() => {
    if (marketContext?.currentPrice != null && !priceEditedByUser) {
      setMarketPrice(marketContext.currentPrice);
      logger.debug(
        "Market price set from parent marketContext",
        { method: "useEffect" },
        { currentPrice: marketContext.currentPrice },
      );
    }
  }, [marketContext, priceEditedByUser, logger]);

  // Fetch market data by symbol via HTTP (adding case without tradeId and no parent marketContext)
  // Apollo lazy query for market quote by symbol
  const [getMarketQuote, { data: marketQuoteData, error: marketQuoteError }] =
    useLazyQuery(GET_MARKET_QUOTE);

  // Fetch market data by symbol via HTTP (adding case without tradeId and no parent marketContext)
  useEffect(() => {
    if (open && symbol && !tradeId && !marketContext?.hasMarketData) {
      // Use backend GraphQL marketQuote proxy instead of calling market-data service directly
      setSymbolMarketDataLoading(true);
      setSymbolMarketDataError(null);
      getMarketQuote({ variables: { symbol } });
    }
    // no abort needed for Apollo lazy query
  }, [open, symbol, tradeId, marketContext, logger, getMarketQuote]);

  // Apply results from GraphQL marketQuote
  useEffect(() => {
    if (marketQuoteData?.marketQuote) {
      const mq = marketQuoteData.marketQuote;
      if (mq?.currentPrice != null) {
        setMarketPrice(mq.currentPrice);
      }
      setSymbolMarketDataLoading(false);
    }
    if (marketQuoteError) {
      setSymbolMarketDataError(
        marketQuoteError instanceof Error
          ? marketQuoteError
          : new Error(String(marketQuoteError)),
      );
      setSymbolMarketDataLoading(false);
    }
  }, [marketQuoteData, marketQuoteError]);

  useEffect(() => {
    if (initialValues) {
      setFormData({
        id: initialValues.id,
        type: initialValues.type,
        quantity: initialValues.quantity,
        price: initialValues.price,
        date: initialValues.date,
        notes: initialValues.notes || "",
        status: initialValues.status,
      });
      setPriceEditedByUser(true); // Don't override price for existing positions
    } else {
      // When adding new position, reset price tracking
      setFormData({
        type: "ENTRY",
        quantity: 0,
        price: 0,
        date: new Date(),
        notes: "",
      });
      setPriceEditedByUser(false); // Allow market price to be applied
    }

    // Clear validation when form opens/closes
    clearValidation();
  }, [initialValues, open, clearValidation]);

  // Update price field when market price arrives (only for new positions that haven't been edited)
  useEffect(() => {
    if (
      marketPrice != null &&
      !initialValues &&
      !priceEditedByUser &&
      formData.price === 0
    ) {
      setFormData((prev) => ({
        ...prev,
        price: marketPrice,
      }));
    }
  }, [marketPrice, initialValues, priceEditedByUser]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    logger.debug(
      "Submitting position form with data",
      { method: "handleSubmit" },
      { formData },
    );

    // Check validation before submitting
    if (validation && !validation.isValid) {
      logger.error(
        "Position form validation failed",
        { method: "handleSubmit" },
        { validation },
      );
      return;
    }

    if (formData.quantity && formData.price && formData.date) {
      logger.debug("Position form validation passed, calling onSave", {
        method: "handleSubmit",
      });
      onSave(formData);
      onClose();
    } else {
      logger.error(
        "Position form validation failed",
        { method: "handleSubmit" },
        {
          quantity: formData.quantity,
          price: formData.price,
          date: formData.date,
        },
      );
      alert("Please fill in all required fields (quantity, price, and date)");
    }
  };

  const handleChange =
    (field: keyof PositionFormValues) =>
    (
      e: React.ChangeEvent<HTMLInputElement> | { target: { value: unknown } },
    ) => {
      const value = e.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]:
          field === "quantity" || field === "price" ? Number(value) : value,
      }));

      // Track when user edits the price field to prevent market price from overriding
      if (field === "price") {
        setPriceEditedByUser(true);
      }
    };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setFormData((prev) => ({
        ...prev,
        date: date,
      }));
    }
  };

  const handleDeposit = async (amount: number) => {
    try {
      // This would trigger the actual deposit mutation
      logger.debug(
        `Depositing $${amount} to wallet`,
        { method: "handleDeposit" },
        { amount },
      );
      // In real implementation, call the deposit mutation here
      // await depositToWallet({ variables: { amount, walletId: trade.walletId } });
    } catch (error) {
      logger.error("Deposit failed", { method: "handleDeposit" }, error);
    }
  };

  // Get dynamic fee settings from user data
  const defaultPositionFee = userData?.me?.defaultPositionFee ?? 0;
  const feeCurrency = userData?.me?.feeCurrency ?? "USD";

  // Check if form can be submitted
  const canSubmit =
    formData.quantity > 0 &&
    formData.price > 0 &&
    formData.date &&
    !isTradeClosed &&
    (!validation || validation.isValid) &&
    !validationLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialValues ? "Edit Position" : "Add Position"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Trade Status Indicator */}
            {tradeId !== undefined && (
              <TradeStatusIndicator
                tradeId={tradeId}
                isClosed={isTradeClosed}
              />
            )}

            {/* Market Data Loading/Info */}
            {(marketDataLoading || symbolMarketDataLoading) && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "info.light",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2" color="info.dark">
                  Fetching current market price...
                </Typography>
              </Box>
            )}

            {(marketDataError || symbolMarketDataError) && (
              <Alert severity="warning">
                Could not fetch market data. Please enter price manually.
              </Alert>
            )}

            {marketPrice != null && !initialValues && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "success.light",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "success.main",
                }}
              >
                <Typography variant="body2" color="success.dark" gutterBottom>
                  Current Market Price
                </Typography>
                <Typography
                  variant="h6"
                  color="success.dark"
                  sx={{ fontWeight: "bold" }}
                >
                  ${marketPrice.toFixed(2)}
                </Typography>
                <Typography
                  variant="caption"
                  color="success.dark"
                  sx={{ fontStyle: "italic" }}
                >
                  Price field has been pre-filled. You can edit if needed.
                </Typography>
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={handleChange("type")}
                label="Type"
                required
                disabled={isTradeClosed}
              >
                {POSITION_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange("quantity")}
              required
              fullWidth
              disabled={isTradeClosed}
              inputProps={{ min: 1 }}
            />

            <TextField
              label="Price"
              type="number"
              inputProps={{ step: 0.01, min: 0.01 }}
              value={formData.price}
              onChange={handleChange("price")}
              required
              fullWidth
              disabled={isTradeClosed}
              helperText={
                marketPrice != null && !initialValues
                  ? `Market price: $${marketPrice.toFixed(2)}`
                  : ""
              }
            />

            <DatePicker
              label="Date"
              value={formData.date}
              onChange={handleDateChange}
              slotProps={{ textField: { fullWidth: true } }}
              disabled={isTradeClosed}
            />

            <TextField
              label="Notes (Optional)"
              value={formData.notes}
              onChange={handleChange("notes")}
              multiline
              rows={3}
              fullWidth
              disabled={isTradeClosed}
            />

            {/* Balance Checker */}
            {formData.quantity > 0 && formData.price > 0 && (
              <BalanceChecker
                tradeId={tradeId}
                quantity={formData.quantity}
                price={formData.price}
                onDeposit={handleDeposit}
              />
            )}

            {/* Validation Status Bar */}
            <ValidationStatusBar validation={validation} />

            <Divider />

            {/* Fee Information */}
            <Typography variant="subtitle2" gutterBottom>
              Fee Information
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Position fees will be automatically calculated and applied based
                on your user settings.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Default Position Fee: {defaultPositionFee}% {feeCurrency}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Fee will be deducted from wallet balance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Fee is included in P&L calculations
              </Typography>
              {userLoading && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  Loading fee settings...
                </Typography>
              )}
            </Box>

            {/* Trade Closed Warning */}
            {isTradeClosed && (
              <Alert severity="warning">
                This trade is closed and cannot be modified. You must reopen the
                trade to make changes.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {validationLoading
              ? "Validating..."
              : initialValues
                ? "Update"
                : "Add"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PositionFormDialog;
